import { createApi } from '@reduxjs/toolkit/query/react';
import debounce from 'lodash/debounce';
import baseQuery from './baseQuery';
import projectApi from './project';
import resultApi from './result';

// One refetch per burst, not per item. These mutations are driven in loops — a
// 50-file upload and a 50-image multi-select delete each fire 50 times — and
// /project re-counts images and re-queries tags for every project on every call.
const INVALIDATE_DEBOUNCE_MS = 800;

// Typed so the mutation argument survives onQueryStarted — an untyped handler
// widens the arg to unknown and silently erases checking on the query body.
interface AnalyzeImageArgs {
    imageId: string;
    engine: string;
    segmentationType: string;
    name: string;
    modelName: string;
    forceAnalyze: boolean;
    resultName: string;
}

interface AnalyzeImagesBatchArgs {
    images: { imageId: string; resultName: string }[];
    engine: string;
    segmentationType: string;
    modelName: string;
    forceAnalyze: boolean;
}

export interface AnalyzeImagesBatchResultItem {
    imageId: string;
    resultName?: string;
    accepted: boolean;
    resultIds?: string[];
    reason?: string;
}

interface AnalyzeImagesBatchResponse {
    batchId: string | null;
    results: AnalyzeImagesBatchResultItem[];
}

// Results and projects live in separate createApi instances, so invalidatesTags
// cannot reach them — the tag has to be dispatched at the other API directly.
const invalidateOnSuccess = (
    api: { util: { invalidateTags: (tags: any) => any } },
    tags: string[]
) => {
    // Created once per endpoint, so the debounce actually coalesces across calls.
    const flush = debounce(
        (dispatch: (action: unknown) => void) => dispatch(api.util.invalidateTags(tags)),
        INVALIDATE_DEBOUNCE_MS
    );
    return async (_arg: unknown, { dispatch, queryFulfilled }: any) => {
        try {
            await queryFulfilled;
            flush(dispatch);
        } catch {
            // the mutation's own isError already surfaces the failure
        }
    };
};

// Define the base API
export const imageApi = createApi({
    reducerPath: 'imageApi', // change the reducer path to 'imageApi'
    baseQuery: baseQuery(),
    tagTypes: ['Image'], // Define a tag type for 'Image' for cache invalidation
    endpoints: (builder) => ({
        // list images API
        listImages: builder.query({
            query: ({ projectId, cursor }) => ({
                url: `/image?projectId=${projectId}${cursor ? `&cursor=${cursor}` : ''}`,
                method: 'GET',
            }),
            providesTags: ['Image'],
            // keepUnusedDataFor: 0, // uncomment this line to disable cache invalidation
        }),
        // create image API
        createImage: builder.mutation({
            query: (body) => ({
                url: `/image/upload-url`,
                method: 'POST',
                body,
            }),
            // Deliberately does not invalidate 'Image': the row is created with
            // upload_status 'URL Generated', and GET /image only returns
            // 'Uploaded' rows, so a refetch here is guaranteed to see nothing.
            // The list stands in for the upload and polls for the real row.
            // /project does count it though, and that count feeds the project
            // header and the home onboarding steps.
            onQueryStarted: invalidateOnSuccess(projectApi, ['Project']),
        }),
        // fetch an image by id
        fetchImage: builder.query({
            query: (id) => ({
                url: `/image/${id}`, // get a specific image by id
            }),
            // same reason as fetchProject: without this a rename is invisible on
            // the image detail page after navigating away and back
            providesTags: ['Image'],
        }),
        analyzeImage: builder.mutation<any, AnalyzeImageArgs>({
            query: (body) => ({
                url: `/image/${body.imageId}/analyze`,
                method: 'POST',
                body,
            }),
            onQueryStarted: invalidateOnSuccess(resultApi, ['Result']),
        }),
        // one call for a whole multi-image submission, replacing the old
        // client-side loop of N sequential analyzeImage calls
        analyzeImagesBatch: builder.mutation<AnalyzeImagesBatchResponse, AnalyzeImagesBatchArgs>({
            query: (body) => ({
                url: `/image/analyze-batch`,
                method: 'POST',
                body,
            }),
            onQueryStarted: invalidateOnSuccess(resultApi, ['Result']),
        }),
        deleteImage: builder.mutation<any, string>({
            query: (id) => ({
                url: `/image/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Image'],
            // /project carries image_count, which the project header and the home
            // onboarding steps read
            onQueryStarted: invalidateOnSuccess(projectApi, ['Project']),
        }),
        updateImage: builder.mutation({
            query: ({ id, name, description }) => ({
                url: `/image/${id}`,
                method: 'PUT',
                body: {
                    id,
                    ...(name && { name }),
                    ...(description && { description })
                },
            }),
            invalidatesTags: ['Image'],
        }),
        // list soft-deleted (trashed) images
        listDeletedImages: builder.query({
            query: () => ({
                url: `/image/deleted`,
            }),
            providesTags: ['Image'],
        }),
        // ponytail: no restore mutation yet — server undo-delete route is broken (app-apis#92)
        // ... you can add more image-related endpoints here
    }),
    // refetchOnMountOrArgChange: true // uncomment this line to disable cache invalidation
});

// Export the auto-generated hooks for use in your components
export const {
    useListImagesQuery,
    useCreateImageMutation,
    useFetchImageQuery,
    useAnalyzeImageMutation,
    useAnalyzeImagesBatchMutation,
    useDeleteImageMutation,
    useUpdateImageMutation,
    useListDeletedImagesQuery,
    // ... other exported queries or mutations
} = imageApi;

export default imageApi;
